import {
  Grid2,
  InputLabel,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";

export type TextFieldProps = Omit<
  MuiTextFieldProps,
  "InputLabelProps" | "margin" | "fullWidth" | "variant" | "color" | "size" | "minRows"
> & {
  name: string;
};

const Label = styled(InputLabel)(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.primary,
  textTransform: "uppercase",
  userSelect: "none",
  marginBottom: theme.spacing(0.5),
}));

const Input = styled(MuiTextField)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,

  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
    borderRadius: 0,
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: theme.palette.secondary.main,
  },

  "& input.MuiInputBase-input": {
    padding: theme.spacing(0, 0, 0.5, 0),
  },

  "& .Mui-error": {
    borderColor: theme.palette.error.main,
    backgroundColor: theme.palette.primary.main,

    "&.MuiFormHelperText-root": {
      background: "none",
    },
  },

  "& .Mui-error input.MuiInputBase-input::placeholder": {
    color: theme.palette.error.main,
    opacity: 1,
  },

  "& .MuiInputBase-root": {
    ...theme.typography.body1,

    "&:focus": {
      outlineOffset: theme.spacing(0.25),
      outlineWidth: theme.spacing(0.25),
      outlineStyle: "solid",
      borderRadius: 0,
    },

    "&.Mui-error:focus": {
      outlineOffset: theme.spacing(0.25),
      outlineWidth: theme.spacing(0.25),
      outlineStyle: "solid",
      outlineColor: theme.palette.primary.main,
      borderRadius: 0,
    },

    "&.Mui-focused, &:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: 0,
        borderBottomWidth: 1,
        borderBottomStyle: "solid",
        borderBottomColor: theme.palette.secondary.main,
      },
    },

    "&.Mui-error:active, &.Mui-error:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderBottomColor: theme.palette.error.main,
      },
    },

    "&.Mui-error:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderWidth: 2,
      },
    },
  },

  "& .MuiInputBase-multiline": {
    display: "flex",
    flexDirection: "column",

    "& .MuiInputBase-inputMultiline": {
      marginBottom: theme.spacing(1),
    },
  },
}));

const LabelAsterisk = styled("span")(({ theme }) => ({
  marginLeft: theme.spacing(0.5),
  color: theme.palette.error.main,

  "&:before": { content: "'*'" },
}));

const TextField = ({ label, name, multiline, id, ...rest }: TextFieldProps) => {
  return (
    <>
      {label && (
        <Label shrink={false} htmlFor={id || name}>
          <Grid2 container display="inline-flex" component="span">
            {label}
            {rest.required && <LabelAsterisk />}
          </Grid2>
        </Label>
      )}
      <Input
        id={id || name}
        fullWidth
        name={name}
        variant="outlined"
        size="small"
        multiline={multiline}
        minRows={multiline && !rest.rows ? 5 : undefined}
        {...rest}
        InputProps={rest.InputProps}
      />
    </>
  );
};

export default TextField;
